import React, { Component, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { crypto, types } from '@binance-chain/javascript-sdk'
import TrezorConnect from 'trezor-connect'

import { Consumer as BNCClientConsumer } from '../context/BNCClientProvider'
import Modal from '../../components/common/Modal'

import { TREZOR_BIP44_PATH } from '../../components/account/unlock/UnlockFromTrezor'

const ModalBody = styled.div`
  color: inherit;
  text-align: center;
`

/**
 * The Trezor signing delegate.
 * @param  {preSignCb}  function
 * @param  {postSignCb} function
 * @param  {errCb} function
 * @return {function}
 */
export const TrezorSigningDelegate = (preSignCb, postSignCb, errCb) => async (tx, signMsg) => {
  if (!tx.accountNumber) {
    errCb(new Error('No account_number was found on the tx'))
    return tx
  }
  let pubKeyHex
  let sigHex
  const signBytes = tx.getSignBytes(signMsg)
  preSignCb && preSignCb(signBytes)
  console.info('Trezor Sign Bytes!', signBytes.toString())
  const trezTx = {
    account_number: +tx.accountNumber,
    chain_id: tx.chainId,
    memo: tx.memo || '',
    sequence: +tx.sequence,
    source: +tx.source,
  }
  const msgType = tx?.msg || tx?.baseMsg
  switch (msgType?.aminoPrefix) {
    case types.AminoPrefix.MsgSend:
      trezTx.transfer = signMsg
      break
    case types.AminoPrefix.NewOrderMsg:
      trezTx.placeOrder = signMsg
      break
    case types.AminoPrefix.CancelOrderMsg:
      trezTx.cancelOrder = signMsg
      break
    default:
      errCb && errCb(new Error(`Trezor does not yet support the '${tx.type}' msg`))
  }
  console.info('Trezor Transaction', trezTx)
  let result
  try {
    result = await TrezorConnect.binanceSignTransaction({
      path: TREZOR_BIP44_PATH,
      transaction: trezTx,
    })
    console.info('Trezor Response!', result)
    pubKeyHex = result.payload.public_key
    sigHex = result.payload.signature
  } catch (err) {
    result = err
  }
  if (!result.success) {
    errCb && errCb(new Error(result.payload.error))
    return tx
  }
  postSignCb && postSignCb(pubKeyHex, sigHex)
  if (pubKeyHex && sigHex) {
    const pubKey = crypto.getPublicKey(pubKeyHex)
    console.info('Trezor Added Signature!', sigHex)
    return tx.addSignature(pubKey, Buffer.from(sigHex, 'hex'))
  }
  errCb && errCb(new Error('Trezor responded without a pubkey or signature'))
  return tx
}

const TrezorDelegateConfigurator = ({ bncClient, preSignCb, postSignCb, errCb, isTrezor }) => {
  // similar to componentDidMount and componentDidUpdate:
  useEffect(() => {
    if (!bncClient || !isTrezor) return
    bncClient.setSigningDelegate(TrezorSigningDelegate(preSignCb, postSignCb, errCb))
  })
  return <div />
}

TrezorDelegateConfigurator.propTypes = {
  bncClient: PropTypes.object.isRequired,
  preSignCb: PropTypes.func,
  postSignCb: PropTypes.func,
  errCb: PropTypes.func,
  isTrezor: PropTypes.bool,
}

TrezorDelegateConfigurator.defaultProps = {
  preSignCb: () => {},
  postSignCb: () => {},
  errCb: () => {},
  isTrezor: false,
}

class TrezorModalHost extends Component {
  static propTypes = {
    isTrezor: PropTypes.bool,
  }

  static defaultProps = {
    isTrezor: false,
  }

  state = { error: null, showModal: false }

  _showSignModal = () => {
    this.setState({ showModal: true })
  }

  _hideSignModal = () => {
    this.setState({ showModal: false })
  }

  _showErrorInModal = err => {
    this.setState({ showModal: false, error: err })
  }

  _clearError = () => {
    this.setState({ error: null })
  }

  render() {
    const { error, showModal } = this.state
    const { isTrezor } = this.props
    return (
      <>
        <BNCClientConsumer>
          {bncClient => (
            <TrezorDelegateConfigurator
              bncClient={bncClient}
              preSignCb={this._showSignModal}
              postSignCb={this._hideSignModal}
              errCb={this._showErrorInModal}
              isTrezor={isTrezor}
            />
          )}
        </BNCClientConsumer>
        {showModal ? (
          <Modal title="Ready to Confirm">
            <ModalBody>Please follow the instructions in Trezor Connect.</ModalBody>
          </Modal>
        ) : null}
        {error ? (
          <Modal close={this._clearError} title="Trezor Error">
            <ModalBody>
              <p>A Trezor integration error occurred:</p>
              <p>
                <i>
                  {error.code}:{error.message}
                </i>
              </p>
              <p>Please ensure that Trezor is open and working.</p>
              <p style={{ marginTop: '1rem' }}>
                <a href="/unlock" rel="noopener">
                  Click here to unlock another wallet
                </a>
              </p>
            </ModalBody>
          </Modal>
        ) : null}
      </>
    )
  }
}

const mapStateToProps = ({
  account: {
    flags: { isTrezor },
  },
}) => ({
  isTrezor,
})

export default connect(mapStateToProps)(TrezorModalHost)
